import { useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { CHECKOUT_PRODUCTS_SYNC_TIMEOUT_MS } from './constants';
import { guestCheckoutSummaryQueryOptions } from './queries';
import { type CheckoutProduct } from './types';
import { useCheckoutProducts } from './useCheckoutProducts';

import { useCurrentLanguage } from '@/modules/language/useCurrentLanguage';

// Sets up a max timeout on the checkout items being populated
// from Magic Link Broadcast Channel - shows skeleton in the meantime
function useIsAwaitingProducts(products: CheckoutProduct[] | undefined) {
  const [hasWaited, setHasWaited] = useState(false);
  const areProductsKnown = products !== undefined;

  useEffect(() => {
    if (areProductsKnown) {
      return;
    }

    const timer = setTimeout(
      () => setHasWaited(true),
      CHECKOUT_PRODUCTS_SYNC_TIMEOUT_MS
    );

    return () => clearTimeout(timer);
  }, [areProductsKnown]);

  return !areProductsKnown && !hasWaited;
}

export function useGuestCheckoutSummary() {
  const { language } = useCurrentLanguage();
  const products = useCheckoutProducts();
  const isAwaitingProducts = useIsAwaitingProducts(products);

  const query = useQuery(
    guestCheckoutSummaryQueryOptions({ products: products ?? [], language })
  );

  if (isAwaitingProducts || query.isLoading) {
    return { status: 'pending' } as const;
  }

  if (!products?.length || !query.data) {
    return { status: 'empty' } as const;
  }

  return {
    status: 'ready',
    products,
    checkoutSummary: query.data,
  } as const;
}
